import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { OrderModel, RequestTemplateDTO, RequestActDTO, StatusDTO } from './model';
import { OrderState } from './state';
import * as selectors from './selectors';
import * as actions from './actions';
import { UserFacade } from '../user/facade';
import { OrderSearchTypeConstants } from 'src/app/core/constants/order-search-type-constants';

@Injectable()
export class OrderFacade {
  private store = inject<Store<OrderState>>(Store);
  private userFacade = inject(UserFacade);

  allOrders$ = this.store.pipe(select(selectors.selectAll));
  acts$ = this.store.pipe(select(selectors.selectActs));
  allowedStatuses$ = this.store.pipe(select(selectors.selectAllowedStatuses));
  ordersForNotification$ = this.store.pipe(select(selectors.selectOrdersForNotification));

  constructor() {
    this.loadByDates(OrderSearchTypeConstants.fromDate, OrderSearchTypeConstants.toDate);
  }

  getById(id: number): Observable<OrderModel> {
    return this.store.pipe(select(selectors.selectById(id)));
  }

  loadByDates(fromDate: Date, toDate: Date) {
    this.userFacade.selected().subscribe(user => {
      if (user) {
        this.store.dispatch(new actions.LoadRequestAction(user.id, fromDate, toDate));
      }
    });
  }

  loadById(id: number) {
    this.userFacade.selected().subscribe(user => {
      if (user) {
        this.store.dispatch(new actions.LoadByIdRequestAction(id, user.id));
      }
    });
  }

  getError(): Observable<any> {
    return this.store.pipe(select(selectors.selectError));
  }

  isLoading(): Observable<boolean> {
    return this.store.pipe(select(selectors.selectIsLoading));
  }

  setFilterType(filterType: string) {
    this.store.dispatch(new actions.SetFilterTypeRequestAction(filterType));
  }

  getFilterType(): Observable<any> {
    return this.store.pipe(select(selectors.selectFilterType));
  }

  selected(): Observable<OrderModel> {
    return this.store.pipe(select(selectors.selectSelected));
  }

  setSelected(id: number) {
    this.userFacade.selected().subscribe(user => {
      if (user) {
        this.store.dispatch(new actions.SetSelectedRequestAction(id, user.id));
      }
    });
  }

  selectedEmployeeForId(): Observable<number> {
    return this.store.pipe(select(selectors.selectSelectedEmployeeForId));
  }

  setSelectedEmployeeForId(id: number) {
    this.userFacade.selected().subscribe(user => {
      if (user) {
        this.store.dispatch(new actions.SetSelectedEmployeeIdRequestAction(id));
      }
    });
  }

  clearSelected() {
    this.store.dispatch(new actions.ClearSelectedRequestAction());
  }

  create(order: OrderModel, files: any[]) {
    this.store.dispatch(new actions.CreateRequestAction(order, files));
  }

  save(order: OrderModel, isChangedOrder: boolean, files: any[]) {
    this.userFacade.selected().subscribe(user => {
      if (user) {
        this.store.dispatch(new actions.SaveRequestAction(order, isChangedOrder, user.id, files));
      }
    });
  }

  loadTemplate(categoryId: number, serviceId: number) {
    this.store.dispatch(new actions.LoadTemplateRequestAction(categoryId, serviceId));
  }

  template(): Observable<RequestTemplateDTO> {
    return this.store.pipe(select(selectors.selectTemplate));
  }

  saveStatus(orderId: number, mark: number, comment: string, files: any[], newStatusId: number, setSelected: boolean) {
    this.userFacade.selected().subscribe(user => {
      if (user) {
        this.store.dispatch(new actions.SaveStatusRequestAction(orderId, mark, user.id, comment, files, newStatusId, setSelected));
      }
    });
  }

  loadActs(id: number) {
    this.store.dispatch(new actions.LoadActsRequestAction(id));
  }

  loadAllowedStatuses(id: number) {
    this.userFacade.selected().subscribe(user => {
      if (user) {
        this.store.dispatch(new actions.LoadAllowedStatusesRequestAction(id, user.id));
      }
    });
  }

  createAct(id: number, value: string) {
    this.userFacade.selected().subscribe(user => {
      if (user) {
        this.store.dispatch(new actions.CreateActRequestAction(id, user.id, value));
      }
    });
  }

  loadForNotification() {
    this.userFacade.selected().subscribe(user => {
      if (user) {
        this.store.dispatch(new actions.LoadForNotificationRequestAction(user.id));
      }
    });
  }

  isLoadingForNotification(): Observable<boolean> {
    return this.store.pipe(select(selectors.selectIsLoadingForNotification));
  }
}
